import { LinkItem, NO_DATA_PLACEHOLDER } from '@occc-app/design-system';
import {
  SubBrand,
  TriggeredMessageListItemFragment,
  TriggeredMessageViewFragment,
} from '@occc/graphql';

import {
  MESSAGING_CENTER_BASE_URL,
  MESSAGING_CENTER_BREADCRUMB_LABEL,
  PAGE_TITLE,
  TRIGGERED_MESSAGES_BASE_URL,
} from './triggered-messages.constants';

export const routeCreator = {
  base: {
    path: `${TRIGGERED_MESSAGES_BASE_URL}/*`,
    to: () => `${TRIGGERED_MESSAGES_BASE_URL}`,
  },
  list: {
    path: '/',
    to: () => `${TRIGGERED_MESSAGES_BASE_URL}`,
  },
  listByScope: {
    path: '/scope/:scope',
    to: (scope: string) => `${TRIGGERED_MESSAGES_BASE_URL}/scope/${scope}`,
  },
  view: {
    path: '/:messageId',
    to: (messageId: string) => `${TRIGGERED_MESSAGES_BASE_URL}/${messageId}`,
  },
  viewScoped: {
    path: '/scope/:scope/:messageId',
    to: (scope: string, messageId: string) =>
      `${TRIGGERED_MESSAGES_BASE_URL}/scope/${scope}/${messageId}`,
  },
  edit: {
    path: '/:messageId/edit',
    to: (messageId: string) => `${TRIGGERED_MESSAGES_BASE_URL}/${messageId}/edit`,
  },
  editScoped: {
    path: '/scope/:scope/:messageId/edit',
    to: (scope: string, messageId: string) =>
      `${TRIGGERED_MESSAGES_BASE_URL}/scope/${scope}/${messageId}/edit`,
  },
};

export const triggeredMessagesSidebarChildLocations = [routeCreator.base.to()];

type GetLinksReturn = {
  current: LinkItem;
  before: LinkItem[];
  links: LinkItem[];
};

export const getListLinks = (): GetLinksReturn => {
  const current: LinkItem = {
    label: PAGE_TITLE,
  };

  const before: LinkItem[] = [
    {
      to: MESSAGING_CENTER_BASE_URL,
      label: MESSAGING_CENTER_BREADCRUMB_LABEL,
    },
  ];

  const links = [...before, current];

  return { current, before, links };
};

export const getViewLinks = (
  message?: TriggeredMessageViewFragment | null,
  subBrand?: SubBrand,
): GetLinksReturn => {
  const current: LinkItem = message
    ? {
        label: message.description ?? 'Details',
      }
    : {
        label: 'Loading...',
      };

  const before: LinkItem[] = [...getListLinks().before];

  if (subBrand) {
    before.push({
      to: routeCreator.listByScope.to(subBrand.uuid),
      label: PAGE_TITLE,
    });
  } else {
    before.push({
      to: routeCreator.list.to(),
      label: PAGE_TITLE,
    });
  }

  const links = [...before, current];

  return { current, before, links };
};

export const getEditLinks = (
  message?: TriggeredMessageViewFragment | null,
  subBrand?: SubBrand,
): GetLinksReturn => {
  const current: LinkItem = message
    ? {
        label: 'Edit',
      }
    : {
        label: 'Loading...',
      };

  const before: LinkItem[] = [...getViewLinks(message, subBrand).before];

  if (message) {
    before.push({
      label: message.description ?? 'Details',
      to: subBrand
        ? routeCreator.viewScoped.to(subBrand.uuid, message.uuid)
        : routeCreator.view.to(message.uuid),
    });
  }

  const links = [...before, current];

  return { current, before, links };
};

type GetTriggeredMessageTypeProps = {
  channels: TriggeredMessageListItemFragment['channelsConfig'] | null;
  joinSymbol?: string;
};

export const getTriggeredMessageType = ({
  channels,
  joinSymbol = '/',
}: GetTriggeredMessageTypeProps): string => {
  const { smsConfig, pushNotificationConfig } = channels || {};

  return (
    [smsConfig?.enabled && 'SMS', pushNotificationConfig?.enabled && 'Push']
      .filter(Boolean)
      .join(` ${joinSymbol} `) || NO_DATA_PLACEHOLDER
  );
};
